import dateFormatter from '@Ravnur-Inc/core/filters/date';
import { Badge$Mode } from '@Ravnur-Inc/core/ui-kit/badge/badge';
import flow from '@Ravnur-Inc/nanoutils/flow';
import head from '@Ravnur-Inc/nanoutils/head';
import { keyById } from '@Ravnur-Inc/nanoutils/keyBy';
import last from '@Ravnur-Inc/nanoutils/last';
import split from '@Ravnur-Inc/nanoutils/string/split';
import { Vue } from 'vue-class-component';

import { StandardOption } from '@/types/Component';
import { ThumbnailableEntity } from '@/types/Entity';
import {
  FieldDescription,
  isSimpleField,
  isEnumerableField,
  FieldDescription$Type,
  isCustomField,
  isRouteField,
  isURLField,
} from '@/types/FieldDescription';

import './formatter-fork.scss';
import { $Props } from '@Ravnur-Inc/core/typings/tsx';

const getDate = flow(split(', '), head);
const getTime = flow(split(', '), last);

const CN = 'formatter-fork';

class Props {
  description: FieldDescription<Entity, string>;
  value: unknown;
  entity?: Entity;
}

export default class FormatterFork extends Vue.with(Props) {
  declare $props: $Props<Props>;

  get isSimpleText(): boolean {
    const { type } = this.description;
    return type === FieldDescription$Type.STRING || type === FieldDescription$Type.THESAURUS;
  }

  get isDate(): boolean {
    return this.description.type === FieldDescription$Type.DATE;
  }

  get isThumbnail(): boolean {
    return this.description.type === FieldDescription$Type.THUMBNAIL;
  }

  get isURL(): boolean {
    return this.description.type === FieldDescription$Type.URL;
  }

  get isRoute(): boolean {
    return isRouteField(this.description);
  }

  get isCustom(): boolean {
    return this.description.type === FieldDescription$Type.CUSTOM;
  }

  get thesaurusAsCache(): Dictionary<StandardOption> {
    const { description } = this;
    return isEnumerableField(description) ? keyById(description.thesaurus) : {};
  }

  get formattedValue(): string {
    const { value, description, thesaurusAsCache: cache, isThumbnail, isCustom } = this;
    const { type } = description;

    if (isThumbnail || isCustom) {
      return '';
    }

    if (isSimpleField(description)) {
      if (description.modifier) {
        return description.modifier(value);
      }

      if ([FieldDescription$Type.STRING].includes(type) && typeof value === 'string') {
        return value;
      }

      if (type === FieldDescription$Type.STRING && typeof value === 'number') {
        return `${value}`;
      }

      if (type === FieldDescription$Type.DATE && typeof value === 'string') {
        return dateFormatter(value, process.env.VUE_APP_OUTPUT_DATE_FORMAT);
      }
    }

    if (isURLField(description)) {
      const host = description.host || '';
      return `${host}${value}`;
    }

    if (isEnumerableField(description)) {
      const v = value instanceof Array ? value : [value];
      return v
        .map((key) => (cache[key] && cache[key].label) || '')
        .filter((_) => _)
        .join(', ');
    }

    this.$logger.warn(`can't convert to type: ${type} from value: `, value);
    return '';
  }

  get date(): string {
    return getDate(this.formattedValue) || '';
  }

  get time(): string {
    return getTime(this.formattedValue) || '';
  }

  get badgeMode(): Nullable<Badge$Mode> {
    const { description, thesaurusAsCache, value } = this;
    if (
      isEnumerableField(description) &&
      (typeof value === 'string' || typeof value === 'number')
    ) {
      const e = thesaurusAsCache[value];
      return (e && e.badge) || null;
    }
    return null;
  }

  get route() {
    const { description } = this;
    return isRouteField(description) ? description.route : null;
  }

  get CustomComponent() {
    const { description } = this;
    if (isCustomField(description)) {
      return description.component;
    }
    return null;
  }

  get customProps(): Dictionary<unknown> {
    const { description } = this;
    if (isCustomField(description)) {
      return description.props || {};
    }
    return {};
  }

  render() {
    if (this.badgeMode) {
      return (
        <badge class={CN} mode={this.badgeMode}>
          {this.formattedValue}
        </badge>
      );
    }

    if (this.isSimpleText) {
      return <span class={CN}>{this.formattedValue}</span>;
    }

    if (this.isDate) {
      return (
        <span class={CN}>
          <span class="nobr">{this.date}</span>
          <br />
          <span class="nobr">{this.time}</span>
        </span>
      );
    }

    if (this.isThumbnail) {
      return <thumbnail class={CN} entity={this.entity as ThumbnailableEntity} />;
    }

    const { CustomComponent } = this;
    if (CustomComponent && this.isCustom) {
      return (
        <CustomComponent class={CN} entity={this.entity} value={this.value} {...this.customProps} />
      );
    }

    if (this.isURL) {
      return (
        <a href={this.formattedValue} rel="noreferrer" target="_blank">
          {this.formattedValue}
        </a>
      );
    }

    if (isRouteField(this.description) && typeof this.route === 'object') {
      const field: keyof Entity = (this.description.field || 'id') as any;

      if (!this.entity?.[field]) {
        return <span>{this.formattedValue}</span>;
      }

      return (
        <router-link to={{ ...this.route, params: { id: this.entity?.[field] || '' } }}>
          {this.formattedValue}
        </router-link>
      );
    }
  }
}
